/**
 * Ring PRO pricing config. Base (full) price per store and currency.
 */
import { Currency } from 'utils/Currency';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import type { TierCampaignData } from './tierCampaignTypes';

/** Base (full) display price per store and currency. One entry per store; all regions using that store see this price. */
export const RING_PRO_BASE_PRICE: Partial<
  Record<ShopifyStore, Partial<Record<Currency, number>>>
> = {
  [ShopifyStore.US]: { [Currency.usd]: 479 },
  [ShopifyStore.ROW]: { [Currency.usd]: 479 },
  [ShopifyStore.CA]: { [Currency.cad]: 659 },
  [ShopifyStore.GB]: { [Currency.gbp]: 429 },
  [ShopifyStore.EU]: { [Currency.eur]: 499 },
  [ShopifyStore.AU]: { [Currency.aud]: 769 },
  [ShopifyStore.IN]: { [Currency.inr]: 44999 },
  [ShopifyStore.AE]: { [Currency.aed]: 1759 },
  [ShopifyStore.SA]: { [Currency.sar]: 2069 },
  [ShopifyStore.MX]: { [Currency.mxn]: 9879 },
  [ShopifyStore.ZA]: { [Currency.zar]: 9269 },
  [ShopifyStore.JP]: { [Currency.jpy]: 77800 },
};

/**
 * Returns the base price for a store/currency.
 */
export function getRingProBasePrice(
  store: ShopifyStore,
  currency: Currency,
): number | undefined {
  return RING_PRO_BASE_PRICE[store]?.[currency];
}

/**
 * Resolves the discount value for a given store from the per-region discount map.
 * Falls back to ROW, then 0. Also handles legacy format where discount_value is a plain number.
 */
export function getDiscountForStore(
  discountValue: Record<string, number> | number,
  store: string,
): number {
  if (typeof discountValue === 'number') return discountValue;
  return discountValue[store] ?? discountValue['ROW'] ?? 0;
}

/** Stub data used until the real API endpoint is available. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TIER_CAMPAIGN_STUB: TierCampaignData = {
  campaign_active: true,
  active_tier: '1',
  total_orders: 0,
  orders_in_current_tier: 0,
  tier_info: {
    '1': {
      max_order_count: 1000,
      discount_value: {
        US: 130,
        ROW: 130,
        CA: 170,
        GB: 110,
        EU: 120,
        AU: 190,
        IN: 10000,
        AE: 470,
        SA: 560,
        MX: 2300,
        ZA: 2000,
      },
      title: 'Super early bird',
    },
    '2': {
      max_order_count: 3000,
      discount_value: {
        US: 90,
        ROW: 90,
        CA: 120,
        GB: 70,
        EU: 80,
        AU: 130,
        IN: 6000,
        AE: 320,
        SA: 390,
        MX: 1500,
        ZA: 1220,
      },
      title: 'Early bird',
    },
    '3': {
      max_order_count: 6000,
      discount_value: {
        US: 50,
        ROW: 50,
        CA: 60,
        GB: 40,
        EU: 40,
        AU: 70,
        IN: 3000,
        AE: 170,
        SA: 220,
        MX: 650,
        ZA: 450,
      },
      title: 'Early access',
    },
    '4': {
      max_order_count: null,
      discount_value: {
        US: 30,
        ROW: 30,
        CA: 30,
        GB: 20,
        EU: 10,
        AU: 30,
        IN: 0,
        AE: 100,
        SA: 130,
        MX: 230,
        ZA: 60,
      },
      title: 'Early adopter',
    },
  },
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TIER_CAMPAIGN_API_URL = '/api/web_v1/ring_pro_preorder_pricing';
// const TIER_CAMPAIGN_API_URL = '';

/** Returns inactive tier campaign data (campaign is no longer running). */
export async function fetchTierCampaignData(): Promise<TierCampaignData> {
  // Tier campaign is no longer active — short-circuit without making a network call.
  return {
    campaign_active: false,
    active_tier: '',
    total_orders: 0,
    orders_in_current_tier: 0,
    tier_info: {},
  };

  //  if (!TIER_CAMPAIGN_API_URL) {
  //    return TIER_CAMPAIGN_STUB;
  //  }

  //  try {
  //    const response = await Promise.race([
  //      fetch(API_BASE_URL + TIER_CAMPAIGN_API_URL),
  //      new Promise<never>((_, reject) =>
  //        setTimeout(() => reject(new Error('Tier campaign API timeout')), 5000),
  //      ),
  //    ]);
  //    const json = await response.json();
  //    return json.data as TierCampaignData;
  //  } catch {
  //    return TIER_CAMPAIGN_STUB;
  //  }
}
